import { useId, useState, type FC } from 'react';
import type { PaymentTermsKind } from '@/api';
import { Alert, Button, Dialog, Input, Select } from '@/ui';

import {
  resolveApiErrorMessage,
  resolveApiValidationMessages,
} from '../../lib/apiError';
import {
  ALL_PAYMENT_TERMS_KINDS,
  PAYMENT_TERMS_KINDS,
  type PaymentTerms,
} from '../../lib/paymentTerms';
import { useSetPartnerPaymentTerms } from './api/mutations';

/*
 * Условия оплаты клиента: вид и его параметр - процент частичной предоплаты или
 * дни отсрочки. Поле параметра появляется только у своего вида; проверка на месте
 * повторяет серверную, диапазоны - как в Partner (1-99 %, 1-365 дней).
 */

export interface PaymentTermsDialogProps {
  partnerId: string;
  partnerName: string;
  terms: PaymentTerms;
  onClose: () => void;
}

const NONE = '';

export const PaymentTermsDialog: FC<PaymentTermsDialogProps> = ({
  partnerId,
  partnerName,
  terms,
  onClose,
}) => {
  const kindId = useId();
  const valueId = useId();
  const [kind, setKind] = useState<PaymentTermsKind | typeof NONE>(
    terms.paymentTerms ?? NONE,
  );
  const [percent, setPercent] = useState(
    terms.prepaymentPercent == null ? '' : String(terms.prepaymentPercent),
  );
  const [days, setDays] = useState(
    terms.paymentDeferralDays == null ? '' : String(terms.paymentDeferralDays),
  );
  const save = useSetPartnerPaymentTerms();

  const percentValue = Number(percent);
  const daysValue = Number(days);
  const isValid =
    kind === 'PartialPrepayment'
      ? Number.isInteger(percentValue) &&
        percentValue >= 1 &&
        percentValue <= 99
      : kind === 'Deferred'
        ? Number.isInteger(daysValue) && daysValue >= 1 && daysValue <= 365
        : true;
  const validationMessages = resolveApiValidationMessages(save.error);

  const submit = () =>
    save.mutate(
      {
        path: { id: partnerId },
        body: {
          paymentTerms: kind === NONE ? null : kind,
          prepaymentPercent: kind === 'PartialPrepayment' ? percentValue : null,
          paymentDeferralDays: kind === 'Deferred' ? daysValue : null,
        },
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Условия оплаты: ${partnerName}`}
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={!isValid || save.isPending}
            onClick={submit}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <p className="m-0 mb-3.5 text-[13px] text-ink-600">
        В 1С условия по клиентам не ведутся - они живут здесь. По ним карточка
        отгрузки считает предоплату до заказа.
      </p>
      <div className="grid gap-3.5 sm:grid-cols-[minmax(0,1fr)_160px]">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={kindId} className="text-xs font-medium text-ink-700">
            Условия
          </label>
          <Select
            id={kindId}
            value={kind}
            onChange={(event) =>
              setKind(event.target.value as PaymentTermsKind | typeof NONE)
            }
          >
            <option value={NONE}>Не заданы</option>
            {ALL_PAYMENT_TERMS_KINDS.map((item) => (
              <option key={item} value={item}>
                {PAYMENT_TERMS_KINDS[item]}
              </option>
            ))}
          </Select>
        </div>
        {kind === 'PartialPrepayment' && (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={valueId}
              className="text-xs font-medium text-ink-700"
            >
              Предоплата, %
            </label>
            <Input
              id={valueId}
              type="number"
              min={1}
              max={99}
              value={percent}
              onChange={(event) => setPercent(event.target.value)}
            />
          </div>
        )}
        {kind === 'Deferred' && (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={valueId}
              className="text-xs font-medium text-ink-700"
            >
              Отсрочка, дней
            </label>
            <Input
              id={valueId}
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(event) => setDays(event.target.value)}
            />
          </div>
        )}
      </div>
      {save.isError && (
        <Alert tone="error" className="mt-3.5">
          {validationMessages.length > 0
            ? validationMessages.join(' ')
            : `Не удалось сохранить: ${resolveApiErrorMessage(save.error)}`}
        </Alert>
      )}
    </Dialog>
  );
};
